// src/app/modules/AccessControl/pages/components/StaffCheckInModal.jsx
import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import * as actions from "../../../Dashbord/_redux/dashboard/Actions";
import { ShieldCheck, QrCode, X, Camera, Smartphone, RefreshCcw, User } from 'lucide-react';
import { Html5QrcodeScanner } from "html5-qrcode";
import QRCode from "react-qr-code";
import "./StaffCheckInModal.scss";

export function StaffCheckInModal({ show, onHide, organizationId, onSuccess, getLabel }) {
  const dispatch = useDispatch();

  const [staffCode, setStaffCode]     = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [checkingIn, setCheckingIn]   = useState(false);
  const [scanMode, setScanMode]       = useState(false);

  // Daily QR payload: "ORGID_YYYY-MM-DD"
  const todayStr    = new Date().toISOString().split('T')[0];
  const dailyQRValue = `${organizationId}_${todayStr}`;

  const { staff } = useSelector(
    (state) => ({ staff: state.dashboard.staff || [] }),
    shallowEqual
  );

  useEffect(() => {
    if (show && organizationId) {
      dispatch(actions.fetchAllData('staff', organizationId));
    }
  }, [show, organizationId, dispatch]);

  useEffect(() => {
    let scanner = null;
    if (scanMode) {
      scanner = new Html5QrcodeScanner("qr-reader", { fps: 10, qrbox: 250 });
      scanner.render((text) => {
        setStaffCode(text);
        setScanMode(false);
        scanner.clear();
      }, () => {});
    }
    return () => { if (scanner) scanner.clear(); };
  }, [scanMode]);

  const handleClose = () => {
    setStaffCode('');
    setSelectedStaff(null);
    setScanMode(false);
    onHide();
  };

  async function handleCheckIn() {
    if (!staffCode && !selectedStaff) return;
    setCheckingIn(true);
    const checkInData = {
      code: staffCode || selectedStaff?.code,
      organization_id: organizationId,
      check_in_method: 'manual',
    };
    try {
      await dispatch(actions.staffCheckIn(checkInData, organizationId));
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setCheckingIn(false);
    }
  }

  const canSubmit = !checkingIn && (!!staffCode || !!selectedStaff);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="staff-checkin-modal">
      <div className="modal-content-custom">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="modal-header-custom">
          <div className="modal-title-custom">
            <div className="title-icon">
              <ShieldCheck size={16} />
            </div>
            <div className="title-text">
              <h3>{getLabel('staff')} Access Terminal</h3>
              <p>Check in staff via QR code or manual entry</p>
            </div>
          </div>
          <button className="btn-close-custom" onClick={handleClose}>
            <X size={15} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="modal-body-custom">
          <div className="scm-columns">

            {/* LEFT — Daily QR for staff to scan with their phone */}
            <div className="scm-qr-panel">
              <div className="scm-qr-panel__label">Self-Service Scan</div>

              <div className="scm-qr-panel__frame">
                <QRCode
                  value={dailyQRValue}
                  size={160}
                  level="H"
                />
              </div>

              <div className="scm-qr-panel__meta">
                <div className="scm-qr-info">
                  <Smartphone size={14} />
                  <span>Scan with Mobile App</span>
                </div>
                <div className="scm-date-badge">
                  Valid: {new Date().toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            </div>

            {/* RIGHT — Guard / manual entry */}
            <div className="scm-manual-panel">
              <div className="scm-manual-panel__title">Manual Verification</div>
              <p className="scm-manual-panel__sub">
                For staff without the app or guard-assisted entry.
              </p>

              <div className="scm-field">
                <label>
                  <QrCode size={12} />
                  Staff ID / QR Payload
                </label>

                {scanMode ? (
                  <div className="scm-scanner">
                    <div id="qr-reader" />
                    <button className="scm-scanner__cancel" onClick={() => setScanMode(false)}>
                      Cancel Scan
                    </button>
                  </div>
                ) : (
                  <div className="scm-input-row">
                    <input
                      type="text"
                      className="scm-input"
                      placeholder="Enter ID or paste QR data"
                      value={staffCode}
                      onChange={(e) => {
                        setStaffCode(e.target.value);
                        setSelectedStaff(null);
                      }}
                    />
                    <button
                      className="scm-scan-btn"
                      title="Scan staff QR"
                      onClick={() => setScanMode(true)}
                    >
                      <Camera size={16} />
                    </button>
                  </div>
                )}
              </div>

              {selectedStaff && (
                <div className="scm-selected-staff">
                  <User size={16} />
                  <div className="info">
                    <strong>{selectedStaff.name}</strong>
                    <span>{selectedStaff.employee_id}</span>
                  </div>
                  <button className="clear" onClick={() => setSelectedStaff(null)}>
                    <X size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="modal-footer-custom">
          <div className="footer-status">
            <RefreshCcw size={13} />
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="footer-actions">
            <button className="sety-btn sety-btn-ghost" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleCheckIn}
              disabled={!canSubmit}
            >
              {checkingIn ? 'Verifying…' : 'Check In Now'}
            </button>
          </div>
        </div>

      </div>
    </Modal>
  );
}